/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { AuthContext } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, Link } from 'react-router-dom'; // Added Link

/* ---------- helpers ---------- */
const generateId = () =>
  `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const MAKERSTATE_BACKGROUND =
  `Makerstate is a digital, AI‑powered education platform that teaches business and entrepreneurship to artists and creatives. It adapts MIT’s 24‑step "Disciplined Entrepreneurship" methodology for creative ventures, pairing accessible content, powerful case studies, active‑learning exercises, and individualized AI feedback so learners graduate with a practical business plan, investor‑ready pitch deck, and robust financial model. Makerstate fills the largest skills gap identified by the Strategic National Arts Alumni Project—business, financial, and entrepreneurial skills—helping artists build financially sustainable careers.`;

const WORKSHOP_DETAILS_PROMPT = `
The complete library of modules:
ID: 68277f26b38ff89862c6d4db • Workshop 1: Impact & Sustainability
• Topics: Impact definition; exploration vs. impact mindset; sustainability (time, money, support); key eqn Impact = Creation×Commercialization
• Qs: SA: describe your project’s impact; MC: current art income; SA: income goal; CBX: sustainability challenges; MC: weekly hours; SA: practical action this month

ID: 68277f26b38ff89862c6d4e6 • Workshop 2: Commercialization Pathways
• Topics: Why commercialization matters; 5 paths (Employee, Intermediary, Nonprofit, Brand-Driven, IP-Driven) with pros/cons
• Qs: MC: preparedness; SA: one success & one barrier; SA: chosen pathway(s) + why

ID: 68277f26b38ff89862c6d4f1 • Workshop 3: Building a Team
• Topics: Myths about “making it”; core roles (Doer, Developer, Dealer); finding teammates; gap-bridging; comp models (cash/profit-share/equity)
• Qs: CBX: myths heard; MC: desired role; CBX: missing skills; SA: one action this week to strengthen your team
`;

const CHAPTER_CONTENTS = {
  "68277f26b38ff89862c6d4db": `**Title:** Workshop 1: Impact & Sustainability
  **Content Blocks:**

  1. **Content:** What is Impact? Impact is the effect your art has on people and culture—from sparking joy to driving social change.
  2. **Content:** Exploration vs. Impact Mindset: Exploration is about finding your voice; an impact mindset is about using that voice to reach others deliberately.
  3. **Question (short answer):** In one sentence, describe the positive change you hope your current project will make.
  4. **Content:** What is Sustainability? Sustainability means having the time, money, and support to keep sharing your work long-term.
  5. **Question (multiple choice):** Which best describes your current income from art?
     * I live on my art income.
     * I earn some but need more.
     * I earn little but want to grow.
     * I create for enjoyment, not income.
  6. **Question (short answer):** Comfortable yearly income / amount you aim to earn from art (write two numbers, separated by a comma).
  7. **Question (checkbox):** What challenges limit your sustainability?
     * Personal networks
     * Professional networks
     * Personal finances
     * Professional finances
     * Time
  8. **Question (multiple choice):** Hours per week you can dedicate to art?
     * <10
     * 10–15
     * 16–20
     * 21–30
     * 31–40
     * 40+
  9. **Content:** Key Equation: Impact = Creation × Commercialization — quality work multiplied by reach and resources.
  10. **Question (short answer):** Name one practical action you’ll take this month toward greater impact or sustainability.`,
  "68277f26b38ff89862c6d4e6": `**Title:** Workshop 2: Commercialization Pathways
  **Content Blocks:**

  1. **Content:** Why Commercialization Matters: Reaching people and earning revenue keeps your art alive and amplifies its impact.
  2. **Content:** Five Pathways: Employee · Intermediary · Nonprofit · Brand-Driven · IP-Driven.
  3. **Question (multiple choice):** How prepared do you feel to own commercialization?
     * Very prepared
     * Somewhat prepared
     * Unprepared
     * Totally new to this
  4. **Content:** Employee Model – Pros: steady pay, build résumé. Cons: scarce jobs, limited creative control.
  5. **Content:** Intermediary Model – Pros: someone sells for you. Cons: less agency, slow traction.
  6. **Content:** Nonprofit Model – Pros: mission alignment, resources. Cons: scarce funding, tight parameters.
  7. **Content:** Brand-Driven Model – Pros: full control, direct audience. Cons: constant hustle, few passive returns.
  8. **Content:** IP-Driven Model – Pros: scalable, passive income. Cons: long runway, need strategy.
  9. **Question (short answer):** Share one success and one barrier you’ve had getting your work to an audience.
  10. **Question (short answer):** Which pathway (or mix) feels right for your next step, and why?`,
  "68277f26b38ff89862c6d4f1": `**Title:** Workshop 3: Building a Team
  **Content Blocks:**

  1. **Content:** Why Teams Matter: Big visions need more than one skill set—building a team multiplies capacity and insight.
  2. **Content:** Common Myths: “Someone will discover me”, “Focus only on art”, “Gatekeepers hold the keys”, “Arts don’t make money.”
  3. **Question (checkbox):** Which of these myths have you heard?
     * Someone will discover me.
     * Focus only on art.
     * Gatekeepers hold the keys.
     * Arts don’t make money.
  4. **Content:** Core Roles: Doer-Developer-Dealer – Doer makes the art, Developer connects it to audiences, Dealer drives money & deals.
  5. **Question (multiple choice):** Which role do you primarily want to fill?
     * Doer
     * Developer
     * Dealer
  6. **Content:** Finding Teammates: Start with your networks, share your vision openly, and ask for referrals.
  7. **Content:** Mind the Gaps: Short-, mid-, and long-term plans help when skills are missing—temporary hires and advisors can bridge gaps.
  8. **Question (checkbox):** Which skills are you currently missing?
     * Strategy
     * Marketing/Sales
     * Project management
     * Fundraising/Finance
     * Production skills
  9. **Content:** Compensation Basics: Combine cash, profit-share, or equity to align everyone’s incentives and capacity.
  10. **Question (short answer):** One concrete step you’ll take this week to strengthen your team (e.g., message a potential collaborator).`
};

// IMPORTANT: You'll need to pass the text of the currently viewed content block to this component.
// For example, if MakerstateAgent is rendered by a parent component that manages chapter content display,
// that parent should pass a prop like `currentContentBlockText`.
const MakerstateAgent = ({ currentContentBlockText, currentFeedback }) => {
  const { token, isAuthenticated, user } = useContext(AuthContext); // Ensure 'user' is destructured
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const [chatMode, setChatMode] = useState('text'); // 'text' or 'audio'
  const [isListening, setIsListening] = useState(false); // From original
  const recognitionRef = useRef(null); // From original
  const synthRef = useRef(null); // For TTS // From original
  const location = useLocation(); // From original & snippet
  const [contextualPrompts, setContextualPrompts] = useState([]); // From original

  useEffect(() => {
    // Initialize SpeechSynthesis and add cleanup
    synthRef.current = window.speechSynthesis;
    const currentSynth = synthRef.current;
    return () => {
      if (currentSynth?.speaking) {
        currentSynth.cancel();
      }
    };
  }, []);

  // Text-to-Speech function
  const speak = useCallback((text) => {
    if (chatMode !== 'audio' || !text || !synthRef.current || !synthRef.current.speak) {
      return;
    }
    
    if (synthRef.current.speaking) {
      synthRef.current.cancel();
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1;
    utterance.pitch = 1;
    
    if (synthRef.current.paused) {
        synthRef.current.resume();
    }
    synthRef.current.speak(utterance);
  }, [chatMode]);

  useEffect(() => {
    if (!isAuthenticated) {
      setContextualPrompts([]);
      return;
    }

    const path = location.pathname;
    if (path === '/profile' || path.startsWith('/profile/')) {
      setContextualPrompts([
        "What's my next recommended module?",
        "Summarize my progress so far.",
        "Suggest areas for improvement based on my work."
      ]);
      console.log(user);
    } else if (path.startsWith('/chapter/')) {
      setContextualPrompts([
        "Can you elaborate on the current topic?",
        "Give me an example related to this chapter.",
        "What are the key learning objectives here?"
      ]);
    } else if (path.startsWith('/feedback/')) {
      setContextualPrompts([
        "Can you explain this feedback in simpler terms?",
        "What are the key takeaways from my feedback?",
        "How can I improve based on this feedback?"
      ]);
    } else if (path.startsWith('/admin')) {
      setContextualPrompts([
        "How do I manage chapters?",
        "Where can I see user statistics?",
      ]);
    } else {
      setContextualPrompts([]);
    }
  }, [location.pathname, isAuthenticated]);

  /* ------------------------------------------------------------
   * call OpenAI via your backend
   * ---------------------------------------------------------- */
  const makeApiCall = async (historyForAPI) => {
    setIsTyping(true);
    setUserInput('');

    let dynamicSystemPrompt =
      `You are Oberon, the Makerstate assistant. ${MAKERSTATE_BACKGROUND} ` +
      `Be very concise, helpful, and encouraging. Don't use bold or italics. That means no asterisks`;

    if (user?.name) {
      dynamicSystemPrompt += ` The user's name is ${user.name}.`;
    }
    // Add user's AI summary if available
    if (user?.aiSummary && user.aiSummary.trim() !== "" && user.aiSummary !== "User has not provided any feedback yet for a summary." && user.aiSummary !== "User has not provided enough information yet for a summary.") {
      dynamicSystemPrompt += `\nHere's a brief summary about the user, based on their reflections: ${user.aiSummary}\n`;
    }

    // Add user's completed chapters to the system prompt
    if (user?.completedChapters && Array.isArray(user.completedChapters) && user.completedChapters.length > 0) {
      dynamicSystemPrompt += ` The user has completed the following chapters: ${user.completedChapters.join(', ')}.`;
    } else if (user) {
      dynamicSystemPrompt += ` The user has not completed any chapters yet.`;
    }

    // Add workshop details to the system prompt
    dynamicSystemPrompt += WORKSHOP_DETAILS_PROMPT;

    const currentPath = location.pathname;
    let chapterId = null;

    if (currentPath === '/profile' || currentPath.startsWith('/profile/')) {
      dynamicSystemPrompt +=
        ' The user is on their profile page (modules & progress). Tailor advice accordingly.';
    } else if (currentPath.startsWith('/chapter/')) {
      chapterId = currentPath.split('/').pop();
      dynamicSystemPrompt += ` The user is currently viewing a chapter (ID: ${chapterId}).`;

      if (currentContentBlockText && currentContentBlockText.trim() !== "") {
        dynamicSystemPrompt += ` They are looking at the following content block:\n"${currentContentBlockText}"\nFocus your assistance on this specific content.`;
      } else {
        const chapterDetails = CHAPTER_CONTENTS[chapterId];
        let chapterTitle = `Chapter ID ${chapterId}`;
        if (chapterDetails) {
            const titleMatch = chapterDetails.match(/\*\*Title:\*\*\s*(.*)/);
            if (titleMatch && titleMatch[1]) {
                chapterTitle = titleMatch[1].trim();
            }
        }
        dynamicSystemPrompt += ` They are on the page for "${chapterTitle}". Specific content block text is not currently available for direct focus. Please assist with general questions about this chapter or related topics.`;
      }
    } else if (currentPath.startsWith('/feedback/')) {
      chapterId = currentPath.split('/').pop();
      dynamicSystemPrompt += ` The user is viewing their feedback for chapter (ID: ${chapterId}).`;
      
      // Add the current feedback to the system prompt
      if (currentFeedback && currentFeedback.trim() !== "") {
        dynamicSystemPrompt += ` Here is the feedback they are viewing:\n"${currentFeedback}"\nFocus your assistance on helping them understand and apply this feedback.`;
      } else {
        dynamicSystemPrompt += ` They are on the feedback page but no feedback content is currently available.`;
      }
    } else if (currentPath.startsWith('/admin')) {
      dynamicSystemPrompt +=
        ' The user is an admin viewing admin tools. Assist with platform-management queries.';
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/chat/message`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            systemPrompt: dynamicSystemPrompt,
            messages: historyForAPI,
            chapterId, // Current chapter's ID for backend processing
            userName: user?.name,
            // You could also send completed chapter IDs explicitly if your backend needs them
            // completedChapterIds: user?.completedChapters?.map(ch => ch._id) 
          }),
        }
      );

      const data = await response.json();
      const assistantMessageId = generateId();

      if (response.ok) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: '', id: assistantMessageId },
        ]);
        await streamAssistantMessage(data.message, assistantMessageId);
      } else {
        const errorMsg =
          data.error || 'Sorry, I ran into a problem. Please try again.';
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: errorMsg, id: assistantMessageId },
        ]);
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setIsTyping(false);
    }
  };

  // Helper function to stream the assistant's message
  const streamAssistantMessage = useCallback(async (fullText, messageId) => {
    return new Promise((resolve) => {
      let charIndex = 0;
      const streamingSpeed = 15; 

      if (fullText.length === 0) {
        setMessages(prevMessages => prevMessages.map(msg => 
          msg.id === messageId ? { ...msg, content: '...' } : msg
        ));
        if (chatMode === 'audio') speak('...');
        resolve();
        return;
      }

      const intervalId = setInterval(() => {
        if (charIndex < fullText.length) {
          setMessages(prevMessages => {
            return prevMessages.map(msg => {
              if (msg.id === messageId && msg.role === 'assistant') {
                return {
                  ...msg,
                  content: fullText.substring(0, charIndex + 1),
                };
              }
              return msg;
            });
          });
          charIndex++;
        } else {
          clearInterval(intervalId);
          if (chatMode === 'audio') {
            speak(fullText);
          }
          resolve();
        }
      }, streamingSpeed);
    });
  }, [chatMode, speak]);

  useEffect(() => {
    const streamInitialMessage = async (text) => {
      setIsTyping(true);
      const newMessageId = generateId();
      setMessages([{ role: 'assistant', content: '', id: newMessageId }]);
      await streamAssistantMessage(text, newMessageId);
      setIsTyping(false);
    };

    if (isAuthenticated) {
      if (user?.role === 'student2') {
        streamInitialMessage("Upgrade to Creator Pro to unlock Oberon AI and get personalized assistance with your creative business!");
        return;
      }
      const path = location.pathname;
      if (path.startsWith('/chapter/')) {
        streamInitialMessage("Please feel free to ask me anything about the chapter and its exercises.");
      } else if (path.startsWith('/feedback/')) {
        streamInitialMessage("Please feel free to ask me anything about your feedback.");
      } else {
        streamInitialMessage('Hello! How may I assist you today?');
      }
    } else {
      streamInitialMessage('Please log in to use the Makerstate Agent.');
    }
  }, [isAuthenticated, user, streamAssistantMessage, location.pathname]); // Added user to dependency array

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Speech Recognition Setup
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.warn('Speech recognition not supported by this browser.');
      if (chatMode === 'audio') setChatMode('text');
      return;
    }

    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognitionAPI();
    const recognition = recognitionRef.current;

    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }
      setUserInput(finalTranscript + interimTranscript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        alert('Microphone access was denied. Please allow microphone access in your browser settings and refresh.');
        setChatMode('text');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    return () => {
      recognition?.abort();
    };
  }, [chatMode]);

  const handleToggleListen = () => {
    if (!recognitionRef.current || chatMode !== 'audio') return;

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      setUserInput('');
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error("Error starting speech recognition:", error);
        setIsListening(false);
        if (error.name === 'NotAllowedError') {
           alert('Microphone access was denied. Please allow microphone access in your browser settings.');
           setChatMode('text');
        }
      }
    }
  };
  
  const toggleChatMode = () => {
    setChatMode(prevMode => {
      const newMode = prevMode === 'text' ? 'audio' : 'text';
      if (newMode === 'text') {
        if (isListening && recognitionRef.current) {
          recognitionRef.current.stop();
          setIsListening(false);
        }
        if (synthRef.current?.speaking) {
          synthRef.current.cancel();
        }
      }
      return newMode;
    });
  };

  const handleSendMessage = async (messageContent = userInput) => {
    const textToSend = typeof messageContent === 'string' ? messageContent.trim() : userInput.trim();
    if (!textToSend || !isAuthenticated || isTyping) return;

    const userMessageId = generateId();
    const newUserMessage = { role: 'user', content: textToSend, id: userMessageId };

    const updatedMessagesWithUser = [...messages, newUserMessage];
    setMessages(updatedMessagesWithUser);

    const messageHistoryForAPI = updatedMessagesWithUser
      .filter(msg => msg.role === 'user' || msg.role === 'assistant')
      .map(m => ({ role: m.role, content: m.content }));

    // Call the standalone makeApiCall function defined above
    makeApiCall(messageHistoryForAPI);
  };

  const handlePromptClick = (prompt) => {
    setUserInput(prompt);
    handleSendMessage(prompt);
  };

  return (
    <div className="fixed top-16 right-0 h-[calc(100vh-4rem)] w-1/3 bg-white border-l border-gray-200 text-gray-800 flex flex-col z-[60] font-body">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-lg font-body text-gray-900">Oberon 🧚‍♂️</h2>
        {isAuthenticated && (
          <button
            onClick={toggleChatMode}
            title={`Switch to ${chatMode === 'text' ? 'Audio' : 'Text'} Mode`}
            className="hidden p-1.5 rounded-md hover:bg-gray-100 text-gray-600 hover:text-gray-800 transition-colors"
          >
            {chatMode === 'text' ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15c-2.21 0-4-1.79-4-4V5c0-2.21 1.79-4 4-4s4 1.79 4 4v6c0 2.21-1.79 4-4 4z" /></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.393-.039.801-.039 1.194 0 1.13.094 1.976 1.057 1.976 2.192V7.5M8.25 7.5h7.5M8.25 7.5V9c0 .53.42.96.938.998l.007.002h5.11c.518 0 .938-.42.938-.938l-.002-.007V7.5M12 21v-6.75A3.375 3.375 0 008.625 11.25H8.25" /></svg>
            )}
          </button>
        )}
      </div>

      <div className="flex-grow overflow-y-auto p-4 space-y-3">
        <AnimatePresence initial={false}>
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              layout
            >
              <div
                className={`max-w-[90%] px-3 py-2 rounded-lg shadow-sm ${
                  msg.role === 'user' ? 'bg-customGreen text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                <p className="text-md whitespace-pre-wrap">{msg.content}</p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {isTyping && messages.length > 0 && messages[messages.length -1]?.role === 'user' && (
           <div className="flex justify-start">
             <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                className="max-w-[90%] px-3 py-2 rounded-lg bg-gray-100 text-gray-700 shadow-sm">
               <p className="text-md animate-pulse">Typing...</p>
             </motion.div>
           </div>
         )}
        <div ref={messagesEndRef} />
      </div>

      {isAuthenticated && (
        <>
          {user?.role === 'student2' ? (
            <div className="p-4 border-t border-gray-200 bg-white text-center">
              <p className="text-md text-gray-600 mb-4">
                Upgrade to Creator Pro to get personalized assistance from Oberon, your AI-powered creative business partner.
              </p>
              <Link
                to="/pricing"
                className="inline-block bg-customGreen text-white px-6 py-2.5 rounded-md font-body"
              >
                View Pro Plan
              </Link>
            </div>
          ) : (
            <div className="p-4 border-t border-gray-200 bg-white">
              {contextualPrompts.length > 0 && (
                <div className="mb-4">
                  <p className="text-md text-gray-500 mb-2">Try asking:</p>
                  <div className="flex flex-wrap gap-2">
                    {contextualPrompts.map((prompt, index) => (
                      <button
                        key={index}
                        onClick={() => handlePromptClick(prompt)}
                        className="text-md bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1 rounded-md transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-2">
                {chatMode === 'audio' && (
                  <button
                    onClick={handleToggleListen}
                    disabled={isTyping}
                    title={isListening ? "Stop listening" : "Start listening"}
                    className={`p-2 rounded-full ${isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors disabled:opacity-50`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15c-2.21 0-4-1.79-4-4V5c0-2.21 1.79-4 4-4s4 1.79 4 4v6c0 2.21-1.79 4-4 4z" />
                    </svg>
                  </button>
                )}
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !isTyping && handleSendMessage()}
                  placeholder={
                    chatMode === 'audio' 
                      ? (isListening ? "Listening..." : "Speak or type...") 
                      : "Ask anything..."
                  }
                  className="text-md flex-grow p-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none"
                  disabled={isTyping || (chatMode === 'audio' && isListening && !userInput)}
                />
                <button
                  onClick={() => handleSendMessage()}
                  disabled={isTyping || !userInput.trim()}
                  className="text-md bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Send
                </button>
              </div>
            </div>
          )}
        </>
      )}
       {!isAuthenticated && (
        <div className="p-4 text-center text-md text-gray-500 bg-gray-50 border-t border-gray-200">
          Please log in to chat with the Makerstate Agent.
        </div>
      )}
    </div>
  );
};

export default MakerstateAgent;